// Payment Service for Stripe and Razorpay Integration
// Handles payment processing, verification, and recording

import { doc, getDoc } from 'firebase/firestore';
import { recordPayment, loadSubscriptionPlan } from '../utils/subscriptionUtils';
import { backofficeDb } from '../backofficeFirebaseClient';

/**
 * Initialize Stripe payment session
 * @param {number} amount - Amount in rupees
 * @param {Object} metadata - Payment metadata (companyId, type, etc.)
 * @returns {Promise<Object>} - Stripe session details
 */
export const initializeStripePayment = async (amount, metadata) => {
  try {
    console.log('🔄 Initializing Stripe payment:', { amount, metadata });

    // TODO: Implement actual Stripe integration
    // This is a placeholder for the actual implementation
    
    const response = await fetch('/api/stripe/create-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: amount * 100, // Convert to paisa
        currency: 'inr',
        metadata: {
          ...metadata,
          source: 'acctoo-backoffice'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create Stripe session');
    }

    const data = await response.json();
    
    console.log('✅ Stripe session created:', data.sessionId);
    
    return {
      success: true,
      provider: 'stripe',
      sessionId: data.sessionId,
      sessionUrl: data.url,
      amount,
      metadata
    };
    
  } catch (error) {
    console.error('❌ Error initializing Stripe payment:', error);
    throw error;
  }
};

/**
 * Initialize Razorpay payment order
 * @param {number} amount - Amount in rupees
 * @param {Object} metadata - Payment metadata (companyId, type, etc.)
 * @returns {Promise<Object>} - Razorpay order details
 */
export const initializeRazorpayPayment = async (amount, metadata) => {
  try {
    console.log('🔄 Initializing Razorpay payment:', { amount, metadata });

    // TODO: Implement actual Razorpay integration
    // This is a placeholder for the actual implementation
    
    const response = await fetch('/api/razorpay/create-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: amount * 100, // Convert to paisa
        currency: 'INR',
        notes: {
          ...metadata,
          source: 'acctoo-backoffice'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create Razorpay order');
    }

    const data = await response.json();
    
    console.log('✅ Razorpay order created:', data.orderId);
    
    return {
      success: true,
      provider: 'razorpay',
      orderId: data.orderId,
      amount,
      currency: 'INR',
      metadata
    };
    
  } catch (error) {
    console.error('❌ Error initializing Razorpay payment:', error);
    throw error;
  }
};

/**
 * Verify payment signature (for Razorpay)
 * @param {string} paymentId - Payment ID
 * @param {string} orderId - Order ID
 * @param {string} signature - Payment signature
 * @returns {Promise<boolean>} - True if signature is valid
 */
export const verifyPaymentSignature = async (paymentId, orderId, signature) => {
  try {
    console.log('🔄 Verifying payment signature');

    const response = await fetch('/api/razorpay/verify-signature', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        payment_id: paymentId,
        order_id: orderId,
        signature: signature
      })
    });

    if (!response.ok) {
      throw new Error('Signature verification failed');
    }

    const data = await response.json();
    
    console.log('✅ Signature verified:', data.verified);
    
    return data.verified;
    
  } catch (error) {
    console.error('❌ Error verifying signature:', error);
    return false;
  }
};

/**
 * Record manual payment
 * @param {string} companyId - Company ID
 * @param {Object} paymentData - Payment data
 * @returns {Promise<Object>} - Payment record result
 */
export const recordManualPayment = async (companyId, paymentData) => {
  try {
    console.log('🔄 Recording manual payment:', { companyId, paymentData });

    let payload = { ...paymentData };

    if (paymentData.type === 'plan') {
      const plan = await loadSubscriptionPlan(paymentData.planId);
      if (!plan) {
        throw new Error(
          `Plan not found in catalog for planId "${paymentData.planId}". Configure Plan Catalog before recording plan payments.`
        );
      }
      const planRef = doc(backofficeDb, 'subscription_plans', paymentData.planId);
      const planSnap = await getDoc(planRef);
      const plan_version =
        planSnap.exists() && planSnap.data().version != null ? Number(planSnap.data().version) : null;

      payload = {
        ...payload,
        amount: Number(plan.price),
        planName: plan.name,
        plan_snapshot: {
          name: plan.name,
          price: plan.price,
          limits: plan.limits,
          billing: plan.billing
        },
        ...(plan_version != null && !Number.isNaN(plan_version) ? { plan_version } : {})
      };
    }

    const idempotency_key = `manualpay_${companyId}_${payload.reference || 'noref'}_${payload.date || ''}_${payload.amount}`;

    const paymentRecord = {
      company_id: companyId,
      amount: payload.amount,
      type: payload.type, // 'plan', 'topup', 'upgrade'
      method: payload.method, // 'manual', 'cash', 'bank_transfer', etc.
      reference: payload.reference || '',
      notes: payload.notes || '',
      date: payload.date || new Date().toISOString(),
      status: 'completed',
      recorded_by: payload.recordedBy || {},
      provider: 'manual',
      idempotency_key
    };

    // Add type-specific data
    if (payload.type === 'plan') {
      paymentRecord.planId = payload.planId;
      paymentRecord.planName = payload.planName;
      paymentRecord.plan_snapshot = payload.plan_snapshot;
      if (payload.plan_version != null) {
        paymentRecord.plan_version = payload.plan_version;
      }
    } else if (payload.type === 'topup') {
      paymentRecord.topupId = payload.topupId;
      paymentRecord.topupName = payload.topupName;
    } else if (payload.type === 'upgrade') {
      paymentRecord.fromPlan = payload.fromPlan;
      paymentRecord.toPlan = payload.toPlan;
    }

    const result = await recordPayment(companyId, paymentRecord);
    
    console.log('✅ Manual payment recorded:', result);
    
    return {
      success: true,
      paymentId: result.paymentId,
      paymentData: paymentRecord
    };
    
  } catch (error) {
    console.error('❌ Error recording manual payment:', error);
    throw error;
  }
};

/**
 * Get payment history for a company
 * @param {string} companyId - Company ID
 * @param {number} limit - Number of payments to fetch (default 50)
 * @returns {Promise<Array>} - Array of payment records
 */
export const getPaymentHistory = async (companyId, limit = 50) => {
  try {
    const { getAppFirestore } = await import('../appFirebaseClient');
    const { collection, query, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');
    
    const appDb = getAppFirestore();
    const paymentsRef = collection(appDb, 'payment_history');
    
    const q = query(
      paymentsRef,
      where('company_id', '==', companyId),
      orderBy('recorded_at', 'desc'),
      firestoreLimit(limit)
    );
    
    const snapshot = await getDocs(q);
    const payments = [];
    
    snapshot.forEach(doc => {
      payments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log(`✅ Fetched ${payments.length} payment records for company ${companyId}`);
    
    return payments;
    
  } catch (error) {
    console.error('❌ Error fetching payment history:', error);
    throw error;
  }
};

/**
 * Get all payment history (admin view)
 * @param {number} limit - Number of payments to fetch (default 100)
 * @returns {Promise<Array>} - Array of payment records
 */
export const getAllPaymentHistory = async (limit = 100) => {
  try {
    const { getAppFirestore } = await import('../appFirebaseClient');
    const { collection, query, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');
    
    const appDb = getAppFirestore();
    const paymentsRef = collection(appDb, 'payment_history');
    
    const q = query(
      paymentsRef,
      orderBy('recorded_at', 'desc'),
      firestoreLimit(limit)
    );
    
    const snapshot = await getDocs(q);
    const payments = [];
    
    snapshot.forEach(doc => {
      payments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log(`✅ Fetched ${payments.length} total payment records`);
    
    return payments;
    
  } catch (error) {
    console.error('❌ Error fetching all payment history:', error);
    throw error;
  }
};

/**
 * Process Stripe webhook event
 * @param {Object} event - Stripe webhook event
 * @returns {Promise<Object>} - Processing result
 */
export const processStripeWebhook = async (event) => {
  try {
    console.log('🔄 Processing Stripe webhook:', event.type);

    switch (event.type) {
      case 'checkout.session.completed':
        const session = event.data.object;
        const metadata = session.metadata;
        
        // Record payment
        await recordManualPayment(metadata.companyId, {
          amount: session.amount_total / 100, // Convert from paisa
          type: metadata.type,
          method: 'stripe',
          reference: session.id,
          notes: `Stripe payment for ${metadata.type}`,
          date: new Date(session.created * 1000).toISOString(),
          provider: 'stripe',
          transactionId: session.payment_intent
        });
        
        break;
        
      case 'payment_intent.payment_failed':
        console.error('⚠️ Stripe payment failed:', event.data.object);
        // TODO: Handle failed payment (notify admin, log, etc.)
        break;
        
      default:
        console.log('ℹ️ Unhandled Stripe event type:', event.type);
    }
    
    return { success: true, processed: true };
    
  } catch (error) {
    console.error('❌ Error processing Stripe webhook:', error);
    throw error;
  }
};

/**
 * Process Razorpay webhook event
 * @param {Object} event - Razorpay webhook event
 * @returns {Promise<Object>} - Processing result
 */
export const processRazorpayWebhook = async (event) => {
  try {
    console.log('🔄 Processing Razorpay webhook:', event.event);

    switch (event.event) {
      case 'payment.captured':
        const payment = event.payload.payment.entity;
        const notes = payment.notes;
        
        // Record payment
        await recordManualPayment(notes.companyId, {
          amount: payment.amount / 100, // Convert from paisa
          type: notes.type,
          method: 'razorpay',
          reference: payment.order_id,
          notes: `Razorpay payment for ${notes.type}`,
          date: new Date(payment.created_at * 1000).toISOString(),
          provider: 'razorpay',
          transactionId: payment.id
        });
        
        break;
        
      case 'payment.failed':
        console.error('⚠️ Razorpay payment failed:', event.payload.payment.entity);
        // TODO: Handle failed payment (notify admin, log, etc.)
        break;
        
      default:
        console.log('ℹ️ Unhandled Razorpay event type:', event.event);
    }
    
    return { success: true, processed: true };
    
  } catch (error) {
    console.error('❌ Error processing Razorpay webhook:', error);
    throw error;
  }
};

/**
 * Generate payment receipt
 * @param {Object} paymentRecord - Payment record
 * @returns {Object} - Receipt data
 */
export const generatePaymentReceipt = (paymentRecord) => {
  return {
    receiptId: `RCPT-${paymentRecord.id}`,
    date: paymentRecord.date,
    companyId: paymentRecord.company_id,
    amount: paymentRecord.amount,
    type: paymentRecord.type,
    method: paymentRecord.method,
    reference: paymentRecord.reference,
    status: paymentRecord.status,
    generatedAt: new Date().toISOString()
  };
};

export default {
  initializeStripePayment,
  initializeRazorpayPayment,
  verifyPaymentSignature,
  recordManualPayment,
  getPaymentHistory,
  getAllPaymentHistory,
  processStripeWebhook,
  processRazorpayWebhook,
  generatePaymentReceipt
};



















































